import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock, Mail, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/data/urban";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink flex flex-col">
      <header className="border-b border-hairline bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-1.5 text-ink-soft hover:text-ink text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
          <div className="font-heading font-extrabold tracking-tight text-ink">{BRAND.name}</div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-14">
        <div className="w-full max-w-[420px]">
          <div className="overline text-ink-soft">Admin Console</div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold leading-tight">Sign in to your dashboard</h1>
          <p className="mt-2 text-sm text-ink-soft">Restricted area · manage leads, chats and enquiries.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <label className="block">
              <span className="overline text-ink-soft">Email</span>
              <div className="mt-2 relative">
                <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                <input
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@urbanrenovation.in"
                  data-testid="login-email"
                  className="w-full h-12 pl-10 pr-4 rounded-md border border-hairline bg-white text-ink text-sm focus:outline-none focus:border-ink"
                />
              </div>
            </label>

            <label className="block">
              <span className="overline text-ink-soft">Password</span>
              <div className="mt-2 relative">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
                <input
                  type={show ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  data-testid="login-password"
                  className="w-full h-12 pl-10 pr-12 rounded-md border border-hairline bg-white text-ink text-sm focus:outline-none focus:border-ink"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  aria-label={show ? "Hide password" : "Show password"}
                  data-testid="login-toggle-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-soft hover:text-ink"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>

            {error && (
              <div data-testid="login-error" className="rounded-md border border-hairline bg-canvas px-3 py-2 text-sm text-ink">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full h-12 bg-black text-white rounded-md text-sm font-bold uppercase tracking-[0.14em] hover:bg-ink disabled:opacity-60 transition-colors"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-8 text-xs text-ink-soft leading-relaxed">
            Access restricted to authorised staff of {BRAND.name}. If you need access, contact the site administrator.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Login;
